import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";

import { QuizStats } from "@/context/QuizContext";

import Alerts from "@/components/Alerts";
import QuestionCard from "./components/QuestionCard";
import QuizDetails from "./components/QuizDetails";
import TimeRemaining from "./components/TimeRemaining";
import ProgressDetails from "./components/ProgressDetails";
import SpinnerFullPage from '@/components/loadingPage/SpinnerFullPage';
import QuizEndError from "./components/QuizEndError";
import useLocalStorage from '@/hooks/useLocalStorage';

function QuizPage() {
  const [isSidebarDisabled, setIsSidebarDisabled] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [firstTime, setFirstTime] = useState(true);


  const { courseId, lectureId, quizId, attemptId } = useParams();

  const { getLocalStorage, setLocalStorage, updateLocalStorageValue } = useLocalStorage()


  const { handleSubmit, register, getValues, watch, setValue } = useForm();

  const { questionsState, numberOfQuestions, quizDetail, onSubmitQuiz, startTime, isSubmitting, quizTimeOut, endTime } = QuizStats();

  const quizDuration = quizDetail?.attributes?.quizDuration

  const formValues = getValues();

  const updateAnswersInLocalStorage = useCallback(
    (data) => {
      updateLocalStorageValue(`quiz_${attemptId}_answers`, data);
    },
    [attemptId, updateLocalStorageValue]
  );

  useEffect(function () {
    const savedData = getLocalStorage(`quiz_${attemptId}_answers`);

    if (savedData && firstTime) {
      // Populate form fields with saved answers
      Object.keys(savedData).forEach(key => {
        if (savedData[key])
          setValue(key, savedData[key]);
      });

      setFirstTime(false)
    }

    if (!savedData || savedData === null) {
      // Initialize localStorage with empty data
      setLocalStorage(`quiz_${attemptId}_answers`, formValues, quizDuration);
    }

    if (savedData)
      updateAnswersInLocalStorage(formValues)


  }, [attemptId, quizDuration, formValues, firstTime, updateAnswersInLocalStorage, setValue, setLocalStorage, getLocalStorage])





  useEffect(() => {
    questionsState(formValues);
  }, [formValues, firstTime, questionsState, updateAnswersInLocalStorage]);


  const onSubmit = useCallback(
    (data) => {
      onSubmitQuiz({ data, courseId, lectureId, quizId });
      setSubmitted(true); // Mark the quiz as submitted
    },
    [onSubmitQuiz, courseId, lectureId, quizId]
  );

  useEffect(() => {
    if (!quizTimeOut) {

      let interval; // Declare interval variable

      const checkTimeRemaining = () => {
        const now = new Date().getTime();
        const timeRemaining = endTime - now;

        if (timeRemaining <= 0 && !submitted) {
          clearInterval(interval);
          onSubmit(getValues());
          setSubmitted(true); // Mark the quiz as submitted
        }
      };

      checkTimeRemaining(); // Check immediately after setting up the interval

      interval = setInterval(checkTimeRemaining, 1000); // Assign interval

      return () => clearInterval(interval);
    }
  }, [startTime, quizDetail?.attributes.quizDuration, quizTimeOut, endTime, submitted, onSubmit, getValues]);


  useEffect(() => {
    const updateSidebarState = () => {
      setIsSidebarDisabled(window.matchMedia("(min-width: 1200px)").matches);
    };
    updateSidebarState(); // Set initial state
    const resizeListener = () => {
      updateSidebarState(); // Update state on resize
    };
    window.addEventListener("resize", resizeListener); // Add event listener for screen size changes

    return () => {
      window.removeEventListener("resize", resizeListener); // Cleanup function to remove event listener
    };
  }, []);

  if (quizTimeOut) return <QuizEndError />;


  if (numberOfQuestions === 0) return (
    <div className="flex items-center justify-center h-screen px-10">
      <Alerts
        message={"لايوجد أسئلة في هذا الاختبار"}
        variant={"filled"}
        severity={"error"}

      />
    </div>
  );


  if (isSubmitting) return <SpinnerFullPage />;


  return (
    <>
      <header className="pt-6 text-center lg:pt-12 text-blue-color-primary">
        <h2>{quizDetail?.attributes.name}</h2>
      </header>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid justify-center grid-cols-3 gap-8 px-4 py-20 mx-auto xl:container lg:px-10 md:px-10 bg-gray-color-lighter lg:bg-inherit">
          <div className="col-span-3 mb-8 space-y-8 lg:hidden">
            <TimeRemaining />
            <ProgressDetails isMobile={isSidebarDisabled} />
          </div>
          <QuestionCard register={register} />
          <QuizDetails watchFormValues={watch} onSubmit={handleSubmit(onSubmit)} />
        </div>
      </form>
    </>
  );
}

export default QuizPage;
